/**
 * Substrate-v2 capture post-pass.
 *
 * Takes a snapshot YAML emitted by the augmentation pipeline and decorates
 * each ref'd element line with synthetic `~`-prefixed attrs that downstream
 * consumers (parser, `buildLocator`) recognize:
 *
 *   - `~v2` on the root element — invariant marker for `isSubstrateV2`
 *   - `~sibling-idx=N` per element among siblings sharing the same role+parent
 *   - `~dom-order=N` per ref'd line based on document order
 *
 * Pure string transformation. No DOM access.
 */

import { encodeV2Value } from '../snapshot-analyzer.js';

const REF_PATTERN = /\[ref=\w+\]/;
const LIST_ITEM_PATTERN = /^(\s*)-\s+/;

interface LineInfo {
  /** Original line text. */
  raw: string;
  /** Indent depth (count of leading spaces). */
  indent: number;
  /** Detected role (first word after `- `). `null` for non-element lines. */
  role: string | null;
  /** True when the line carries a `[ref=...]` token. */
  hasRef: boolean;
  /** dom-order index (only assigned to ref'd lines). */
  domOrder: number;
  /** sibling-idx among same-role siblings under the same parent. */
  siblingIdx: number;
  /** Index of the parent line in the lines array, or -1 for root. */
  parentLineIdx: number;
}

/**
 * Substrate-v2 modal role override. Paired with v2's
 * `enrichModalDialogElementsV2`: each detected modal `<div>` already in
 * the a11y tree as `generic` carries an override on the existing line —
 * `~aug-role`/`~aug-name`/`~role-src=augmented:dom-mutation`/`~css` —
 * which the formatter's `effectiveRole(el)` lookup then surfaces as a
 * dialog. No tree mutation, no synthetic-node injection.
 */
export interface V2RoleOverride {
  ref: string;
  augRole: string;
  augName?: string;
  /** Pre-formatted `~css` value (e.g. `div.modal-dialog`). `null` is
   *  accepted so callers can pass through `buildCssHint`'s nullable
   *  result without a manual coalesce. */
  cssHint?: string | null;
}

/**
 * Append role-override synthetic attrs to each ref'd line whose ref appears
 * in the override list. Idempotent: lines that already carry `[~aug-role=…]`
 * are not re-tagged.
 */
export function appendV2RoleOverrides(
  yaml: string,
  overrides: readonly V2RoleOverride[]
): string {
  if (overrides.length === 0) return yaml;
  const byRef = new Map<string, V2RoleOverride>();
  for (const ov of overrides) byRef.set(ov.ref, ov);

  const lines = yaml.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const refMatch = lines[i].match(/\[ref=(\w+)\]/);
    if (!refMatch) continue;
    const ov = byRef.get(refMatch[1]);
    if (!ov) continue;
    if (lines[i].includes('[~aug-role=')) continue;

    const segments: string[] = [`[~aug-role=${encodeV2Value(ov.augRole)}]`];
    if (ov.augName !== undefined) {
      segments.push(`[~aug-name=${encodeV2Value(ov.augName)}]`);
    }
    segments.push('[~role-src=augmented:dom-mutation]');
    if (ov.cssHint) {
      segments.push(`[~css=${encodeV2Value(ov.cssHint)}]`);
    }
    lines[i] = appendAttrsToLine(lines[i], segments.join(''));
  }
  return lines.join('\n');
}

/**
 * Append `[~name-src=augmented:dom-mutation]` to each line whose ref appears
 * in the provenance map. Used after v1's `augmentUnlabeledElements` has
 * injected augmented names — the side-channel map records which refs got
 * which sub-category of augmentation, and v2 capture tags those lines so
 * `buildLocator`'s source-durability gate refuses to use the augmented
 * name as a stable predicate.
 *
 * Idempotent: lines that already carry `[~name-src=...]` are not re-tagged.
 */
export function appendNameSourceProvenance(
  yaml: string,
  augmentedRefs: ReadonlyMap<string, string>
): string {
  if (augmentedRefs.size === 0) return yaml;
  const lines = yaml.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const refMatch = lines[i].match(/\[ref=(\w+)\]/);
    if (!refMatch) continue;
    if (!augmentedRefs.has(refMatch[1])) continue;
    if (lines[i].includes('[~name-src=')) continue;
    lines[i] = appendAttrsToLine(lines[i], '[~name-src=augmented:dom-mutation]');
  }
  return lines.join('\n');
}

/**
 * Append substrate-v2 synthetic attrs to a v1 YAML snapshot.
 *
 * Behaviour:
 *   - Adds `[~v2]` to the first element line so `isSubstrateV2` returns true.
 *   - Adds `[~sibling-idx=N]` (0-based) per element among same-role siblings
 *     under the same parent.
 *   - Adds `[~dom-order=N]` (0-based) per ref'd element based on traversal order.
 *
 * Idempotent: if the YAML already contains `~v2`, the input is returned as-is.
 */
export function appendSubstrateV2DerivedAttrs(yaml: string): string {
  if (yaml.includes('[~v2]') || yaml.includes('[~v2=')) {
    return yaml;
  }

  const lines = yaml.split('\n');
  const infos: LineInfo[] = Array.from({ length: lines.length });

  // Pass 1: classify each line, compute parent index, sibling-idx, dom-order.
  const stack: Array<{ indent: number; lineIdx: number }> = [];
  let domOrder = 0;
  // Sibling counts keyed by `${parentLineIdx}|${role}`.
  const siblingCounts = new Map<string, number>();

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const match = raw.match(LIST_ITEM_PATTERN);
    if (!match) {
      infos[i] = {
        raw,
        indent: 0,
        role: null,
        hasRef: false,
        domOrder: -1,
        siblingIdx: -1,
        parentLineIdx: -1,
      };
      continue;
    }

    const indent = match[1].length;
    const content = raw.slice(match[0].length);
    // Strip any leading single-quote wrapper (YAML uses it when text contains ':').
    const unquoted = content.startsWith("'") ? content.slice(1) : content;
    const roleMatch = unquoted.match(/^(\w+)/);
    const role = roleMatch ? roleMatch[1] : null;
    const hasRef = REF_PATTERN.test(raw);

    // Pop stack until we find a parent at strictly smaller indent.
    while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }
    const parentLineIdx = stack.length > 0 ? stack[stack.length - 1].lineIdx : -1;

    let siblingIdx = -1;
    if (role) {
      const key = `${parentLineIdx}|${role}`;
      siblingIdx = siblingCounts.get(key) ?? 0;
      siblingCounts.set(key, siblingIdx + 1);
    }

    const order = hasRef ? domOrder++ : -1;

    infos[i] = {
      raw,
      indent,
      role,
      hasRef,
      domOrder: order,
      siblingIdx,
      parentLineIdx,
    };

    if (role) {
      stack.push({ indent, lineIdx: i });
    }
  }

  // Pass 2: rebuild lines with appended synthetic attrs.
  let firstElementSeen = false;
  const out: string[] = Array.from({ length: lines.length });
  for (let i = 0; i < lines.length; i++) {
    const info = infos[i];
    if (!info.role) {
      out[i] = info.raw;
      continue;
    }

    const segments: string[] = [];
    if (!firstElementSeen) {
      segments.push('[~v2]');
      firstElementSeen = true;
    }
    if (info.hasRef && info.domOrder >= 0) {
      segments.push(`[~dom-order=${info.domOrder}]`);
    }
    if (info.siblingIdx >= 0) {
      segments.push(`[~sibling-idx=${info.siblingIdx}]`);
    }

    if (segments.length === 0) {
      out[i] = info.raw;
      continue;
    }

    out[i] = appendAttrsToLine(info.raw, segments.join(''));
  }

  return out.join('\n');
}

/**
 * Append synthetic attr segments to a YAML element line, preserving any
 * trailing colon (used for elements with children) and/or value (used for
 * input-like roles such as textbox).
 *
 * Examples:
 *   `- button "Submit" [ref=e1]`           → `- button "Submit" [ref=e1][~v2]`
 *   `- generic [ref=e2]:`                  → `- generic [ref=e2]:[~v2]`  ← wrong
 *   We need to insert *before* the trailing `:` so the parent/child structure
 *   keeps parsing.
 */
function appendAttrsToLine(line: string, segments: string): string {
  // Trailing colon (with optional value after) marks a parent line.
  // YAML lines may end with:
  //   `...]:`        (no value)
  //   `...]: value`  (textbox / value-bearing role)
  //   `...]':`       (quoted parent line)
  //   `...]`         (leaf)
  const colonValueMatch = line.match(/^(.*?\])(')?(:.*)?$/);
  if (colonValueMatch && colonValueMatch[2]) {
    // Quoted YAML element lines wrap the whole element grammar in single quotes.
    // Keep synthetic attrs inside that quote so the parser sees them as element attrs.
    return `${colonValueMatch[1]}${segments}${colonValueMatch[2]}${colonValueMatch[3] ?? ''}`;
  }
  if (colonValueMatch && colonValueMatch[3]) {
    // Insert before `:` so structure is preserved.
    return `${colonValueMatch[1]}${segments}${colonValueMatch[3]}`;
  }
  // Leaf or no recognizable bracket: append at end.
  return `${line}${segments}`;
}
